import { supabase } from '../supabase'
import type {
  ProjectCreateData,
  ProjectUpdateData,
  UserProject,
  AuthError,
} from '../types/auth'

/**
 * Project Management API
 * 
 * Handles user projects, sharing, and collaboration
 */

export class ProjectAPI {
  private static instance: ProjectAPI

  private constructor() {}

  public static getInstance(): ProjectAPI {
    if (!ProjectAPI.instance) {
      ProjectAPI.instance = new ProjectAPI()
    }
    return ProjectAPI.instance
  }

  /**
   * Create a new project
   * POST /api/projects
   */
  async createProject(userId: string, projectData: ProjectCreateData): Promise<{ project: UserProject | null; error: AuthError | null }> {
    try {
      const { data, error } = await supabase
        .from('user_projects')
        .insert({
          user_id: userId,
          title: projectData.title,
          description: projectData.description,
          language: projectData.language || 'blockly',
          difficulty_level: projectData.difficultyLevel || 'beginner',
          is_public: projectData.isPublic || false,
          tags: projectData.tags || [],
          project_data: {}, // Empty initial project data
        })
        .select()
        .single()

      if (error) {
        return {
          project: null,
          error: { message: error.message },
        }
      }

      return {
        project: data,
        error: null,
      }
    } catch (error) {
      return {
        project: null,
        error: { message: error.message || 'Failed to create project' },
      }
    }
  }

  /**
   * Update an existing project
   * PUT /api/projects/:projectId
   */
  async updateProject(projectId: string, userId: string, updates: ProjectUpdateData): Promise<{ success: boolean; error: AuthError | null }> {
    try {
      const updateData: any = {}

      if (updates.title) updateData.title = updates.title
      if (updates.description !== undefined) updateData.description = updates.description
      if (updates.projectData) updateData.project_data = updates.projectData
      if (updates.language) updateData.language = updates.language
      if (updates.difficultyLevel) updateData.difficulty_level = updates.difficultyLevel
      if (updates.isPublic !== undefined) updateData.is_public = updates.isPublic
      if (updates.tags) updateData.tags = updates.tags
      if (updates.thumbnailUrl !== undefined) updateData.thumbnail_url = updates.thumbnailUrl

      const { error } = await supabase
        .from('user_projects')
        .update(updateData)
        .eq('id', projectId)
        .eq('user_id', userId) // Ensure user owns the project

      if (error) {
        return {
          success: false,
          error: { message: error.message },
        }
      }

      return {
        success: true,
        error: null,
      }
    } catch (error) {
      return {
        success: false,
        error: { message: error.message || 'Failed to update project' },
      }
    }
  }

  /**
   * Get user's projects
   * GET /api/projects/user/:userId
   */
  async getUserProjects(userId: string): Promise<UserProject[]> {
    try {
      const { data, error } = await supabase
        .from('user_projects')
        .select('*')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false })

      if (error) {
        console.error('Error fetching user projects:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error fetching user projects:', error)
      return []
    }
  }

  /**
   * Get a specific project
   * GET /api/projects/:projectId
   */
  async getProject(projectId: string, userId?: string): Promise<UserProject | null> {
    try {
      let query = supabase
        .from('user_projects')
        .select('*')
        .eq('id', projectId)

      // If no userId provided, only get public projects
      if (!userId) {
        query = query.eq('is_public', true)
      } else {
        // If userId provided, get public projects or user's own projects
        query = query.or(`is_public.eq.true,user_id.eq.${userId}`)
      }

      const { data, error } = await query.single()

      if (error) {
        console.error('Error fetching project:', error)
        return null
      }

      // Increment view count if it's a public project and not the owner viewing
      if (data && data.is_public && userId && data.user_id !== userId) {
        await this.incrementViewCount(projectId)
      }

      return data
    } catch (error) {
      console.error('Error fetching project:', error)
      return null
    }
  }

  /**
   * Delete a project
   * DELETE /api/projects/:projectId
   */
  async deleteProject(projectId: string, userId: string): Promise<{ success: boolean; error: AuthError | null }> {
    try {
      const { error } = await supabase
        .from('user_projects')
        .delete()
        .eq('id', projectId)
        .eq('user_id', userId) // Ensure user owns the project

      if (error) {
        return {
          success: false,
          error: { message: error.message },
        }
      }

      return {
        success: true,
        error: null,
      }
    } catch (error) {
      return {
        success: false,
        error: { message: error.message || 'Failed to delete project' },
      }
    }
  }

  /**
   * Fork a public project
   * POST /api/projects/:projectId/fork
   */
  async forkProject(projectId: string, userId: string, newTitle?: string): Promise<{ project: UserProject | null; error: AuthError | null }> {
    try {
      // Get the original project
      const originalProject = await this.getProject(projectId)
      
      if (!originalProject || !originalProject.is_public) {
        return {
          project: null,
          error: { message: 'Project not found or not public' },
        }
      }

      // Create a forked copy
      const { data, error } = await supabase
        .from('user_projects')
        .insert({
          user_id: userId,
          title: newTitle || `${originalProject.title} (Fork)`,
          description: `Forked from ${originalProject.title}`,
          project_data: originalProject.project_data,
          language: originalProject.language,
          difficulty_level: originalProject.difficulty_level,
          is_public: false, // Forks start as private
          tags: originalProject.tags,
        })
        .select()
        .single()

      if (error) {
        return {
          project: null,
          error: { message: error.message },
        }
      }

      // Increment fork count on original project
      await this.incrementForkCount(projectId)

      return {
        project: data,
        error: null,
      }
    } catch (error) {
      return {
        project: null,
        error: { message: error.message || 'Failed to fork project' },
      }
    }
  }

  /**
   * Like/unlike a project
   * POST /api/projects/:projectId/like
   */
  async toggleLike(projectId: string, userId: string): Promise<{ liked: boolean; error: AuthError | null }> {
    try {
      // Check if already liked (you'd need a separate likes table in a real implementation)
      // For now, we'll just increment/decrement the likes count
      const project = await this.getProject(projectId, userId)
      
      if (!project || !project.is_public) {
        return {
          liked: false,
          error: { message: 'Project not found or not public' },
        }
      }

      // In a real implementation, you'd have a separate user_project_likes table
      // For now, we'll just increment the likes count
      const { error } = await supabase
        .from('user_projects')
        .update({ likes_count: (project.likes_count || 0) + 1 })
        .eq('id', projectId)

      if (error) {
        return {
          liked: false,
          error: { message: error.message },
        }
      }

      return {
        liked: true,
        error: null,
      }
    } catch (error) {
      return {
        liked: false,
        error: { message: error.message || 'Failed to like project' },
      }
    }
  }

  /**
   * Get public projects (gallery)
   * GET /api/projects/public
   */
  async getPublicProjects(options?: {
    limit?: number
    offset?: number
    language?: string
    difficulty?: string
    tags?: string[]
    featured?: boolean
  }): Promise<UserProject[]> {
    try {
      let query = supabase
        .from('user_projects')
        .select(`
          *,
          user_profiles!user_projects_user_id_fkey(username, display_name, avatar_url)
        `)
        .eq('is_public', true)

      // Apply filters
      if (options?.language) {
        query = query.eq('language', options.language)
      }

      if (options?.difficulty) {
        query = query.eq('difficulty_level', options.difficulty)
      }

      if (options?.featured) {
        query = query.eq('is_featured', true)
      }

      if (options?.tags && options.tags.length > 0) {
        query = query.overlaps('tags', options.tags)
      }

      // Apply pagination
      if (options?.limit) {
        query = query.limit(options.limit)
      }

      if (options?.offset) {
        query = query.range(options.offset, (options.offset + (options.limit || 10)) - 1)
      }

      // Order by popularity (likes + views + recency)
      query = query.order('likes_count', { ascending: false })
        .order('views_count', { ascending: false })
        .order('created_at', { ascending: false })

      const { data, error } = await query

      if (error) {
        console.error('Error fetching public projects:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error fetching public projects:', error)
      return []
    }
  }

  /**
   * Search projects
   * GET /api/projects/search?q=query
   */
  async searchProjects(query: string, options?: {
    limit?: number
    publicOnly?: boolean
    userId?: string
  }): Promise<UserProject[]> {
    try {
      let dbQuery = supabase
        .from('user_projects')
        .select('*')

      // Apply public filter
      if (options?.publicOnly !== false) {
        dbQuery = dbQuery.eq('is_public', true)
      } else if (options?.userId) {
        dbQuery = dbQuery.or(`is_public.eq.true,user_id.eq.${options.userId}`)
      }

      // Search in title, description, and tags
      dbQuery = dbQuery.or(
        `title.ilike.%${query}%,description.ilike.%${query}%,tags.cs.{${query}}`
      )

      if (options?.limit) {
        dbQuery = dbQuery.limit(options.limit)
      }

      dbQuery = dbQuery.order('updated_at', { ascending: false })

      const { data, error } = await dbQuery

      if (error) {
        console.error('Error searching projects:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error searching projects:', error)
      return []
    }
  }

  // Private helper methods

  private async incrementViewCount(projectId: string): Promise<void> {
    try {
      await supabase.rpc('increment_view_count', { project_id: projectId })
    } catch (error) {
      console.error('Error incrementing view count:', error)
    }
  }

  private async incrementForkCount(projectId: string): Promise<void> {
    try {
      await supabase.rpc('increment_fork_count', { project_id: projectId })
    } catch (error) {
      console.error('Error incrementing fork count:', error)
    }
  }
}

// Create and export singleton instance
export const projectAPI = ProjectAPI.getInstance()

// Export individual functions
export const {
  createProject,
  updateProject,
  getUserProjects,
  getProject,
  deleteProject,
  forkProject,
  toggleLike,
  getPublicProjects,
  searchProjects,
} = projectAPI